/**
 * StatusChip — chip de statut cliquable (design system Argalis).
 *
 * Une seule pastille colorée PLEINE qui sert de déclencheur : cliquer dessus
 * déroule les statuts, présentés comme des pills (le menu « écho » du
 * déclencheur), qui remplacent le statut courant.
 *
 * Parti pris visuel (repris d'une maquette PO, adapté à notre stack Radix +
 * couleurs dynamiques + dark mode) :
 *   - **pills pleines** : fond = teinte douce de la couleur du statut, texte =
 *     version encrée lisible. Théme-aware via `color-mix(... hsl(var(--…)))`
 *     (clair ET sombre). Pas de bordure, pas de puce.
 *   - **chevron qui pivote** à l'ouverture.
 *   - **survol = `brightness`** sur la pill elle-même (jamais un fond teinté
 *     DERRIÈRE la pill : un fond vert/gris par-dessus une puce rouge serait
 *     disgracieux — ici l'item EST la pill, il s'assombrit/éclaircit).
 *   - **sélection = liseré interne + coche** (pas de fond différent).
 *   - **entrée en cascade** des items.
 *
 * Générique et transverse : devis (SALE-008) + réutilisable pour les sessions.
 *   - `options`/`onSelect` absents ou `disabled` → chip statique (aucun chevron).
 *
 * Doc : DESIGN_SYSTEM.md § « StatusChip ».
 */

import { Check, ChevronDown } from 'lucide-react'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
// Teintes partagées : tout écran qui répète un statut hors de sa chip doit
// reprendre la MÊME encre (cf. `statusChipColors`).
import { pillStyle, ringColor } from '@/components/shared/statusChipColors'
import { cn } from '@/lib/utils'

export interface StatusChipOption {
    id: string
    label: string
    /** Couleur CSS du statut (hex issu du référentiel). */
    color: string
    /** Couleur de l'ENCRE, quand elle ne se dérive pas de `color` — voir
     *  `pillStyle`. Facultative : sans elle, le rendu d'origine est inchangé. */
    encre?: string
    /** Option affichée mais non sélectionnable (grisée) — ex. transition non
     *  autorisée par le workflow, montrée pour matérialiser l'étape. */
    disabled?: boolean
}

interface StatusChipProps {
    /** Statut courant affiché. */
    current: StatusChipOption
    /** Statuts sélectionnables. Absent/vide → chip statique (lecture seule). */
    options?: StatusChipOption[]
    /** Sélection d'un autre statut (le courant est ignoré / no-op côté appelant). */
    onSelect?: (id: string) => void
    /** Lecture seule ou mutation en cours → chip statique, non cliquable. */
    disabled?: boolean
    /** Densité visuelle. */
    size?: 'sm' | 'md'
    /** Alignement du menu déroulant. */
    align?: 'start' | 'end'
    /** Libellé accessible du déclencheur. */
    ariaLabel?: string
    /**
     * Pied de menu, sous un séparateur — pour un geste de PORTÉE DIFFÉRENTE.
     *
     * Choisir une option déplace UNE fiche ; un pied de menu peut ouvrir la
     * gestion du référentiel, qui engage tout l'organisme. Les deux vivent dans
     * le même menu parce que c'est là qu'on ressent le manque, mais jamais sur
     * le même plan : d'où le séparateur, et jamais d'édition en ligne au milieu
     * des options. L'appelant est responsable du droit — un pied absent quand le
     * droit manque vaut mieux qu'un pied grisé.
     */
    footer?: React.ReactNode
    className?: string
}

const SIZES = {
    sm: 'gap-1 px-2.5 py-0.5 text-xs',
    md: 'gap-1.5 px-3 py-1.5 text-sm',
} as const

/** Pastille statique (lecture seule, cellules de table) — pill pleine sans chevron. */
function ChipPill({
    label,
    color,
    encre,
    size,
    className,
}: {
    label: string
    color: string
    encre?: string
    size: 'sm' | 'md'
    className?: string
}) {
    return (
        <span
            className={cn(
                'inline-flex items-center whitespace-nowrap rounded-full font-medium',
                SIZES[size],
                className,
            )}
            style={pillStyle(color, encre)}
        >
            {label}
        </span>
    )
}

/**
 * Item de menu « pill » — LE rendu d'option de statut du produit.
 *
 * Exporté pour que tout autre menu de statut (ex. le sous-menu ⋮ d'une tuile
 * kanban) affiche EXACTEMENT la même chose que la fiche devis : une seule
 * implémentation, zéro divergence visuelle (retour PO 2026-07-25). Fonctionne
 * aussi bien dans un `DropdownMenuContent` que dans un `DropdownMenuSubContent`.
 */
export function StatusOptionItem({
    option,
    isCurrent = false,
    index = 0,
    onSelect,
}: {
    option: StatusChipOption
    /** Coche + liseré interne. Un menu qui MASQUE le statut courant laisse `false`. */
    isCurrent?: boolean
    /** Rang, pour l'entrée en cascade. */
    index?: number
    onSelect: (id: string) => void
}) {
    const isDisabled = !!option.disabled && !isCurrent
    return (
        <DropdownMenuItem
            disabled={isDisabled}
            onSelect={() => {
                if (!isCurrent && !option.disabled) onSelect(option.id)
            }}
            // Item transparent (aucun fond derrière la pill) + entrée en cascade.
            className={cn(
                'group cursor-pointer rounded-md p-[2px] duration-150 animate-in fade-in-0 slide-in-from-top-1 focus:bg-transparent data-[highlighted]:bg-transparent',
                isDisabled && 'cursor-not-allowed opacity-45',
            )}
            style={{ animationDelay: `${index * 24}ms`, animationFillMode: 'backwards' }}
        >
            <span
                className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2.5 py-1 text-xs font-medium transition-[filter]',
                    !isDisabled &&
                        'group-hover:brightness-95 group-data-[highlighted]:brightness-95 dark:group-hover:brightness-110 dark:group-data-[highlighted]:brightness-110',
                )}
                style={{
                    ...pillStyle(option.color, option.encre),
                    boxShadow: isCurrent ? `inset 0 0 0 1.5px ${ringColor(option.color)}` : undefined,
                }}
            >
                <span className="flex-1 truncate">{option.label}</span>
                {isCurrent && <Check className="h-3.5 w-3.5 shrink-0" />}
            </span>
        </DropdownMenuItem>
    )
}

export function StatusChip({
    current,
    options,
    onSelect,
    disabled,
    size = 'md',
    align = 'start',
    ariaLabel,
    footer,
    className,
}: StatusChipProps) {
    const interactive = !disabled && !!onSelect && !!options && options.length > 0
    const chevronSize = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'

    if (!interactive) {
        return <ChipPill label={current.label} color={current.color} size={size} className={className} />
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    aria-label={ariaLabel}
                    className={cn(
                        'group inline-flex items-center whitespace-nowrap rounded-full font-medium outline-none transition-[filter,box-shadow] hover:brightness-[0.97] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 dark:hover:brightness-110',
                        SIZES[size],
                        className,
                    )}
                    style={pillStyle(current.color, current.encre)}
                >
                    {current.label}
                    <ChevronDown
                        className={cn(
                            'shrink-0 opacity-60 transition-transform duration-200 group-data-[state=open]:rotate-180',
                            chevronSize,
                        )}
                    />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align={align}
                sideOffset={6}
                className="min-w-[9rem] rounded-lg p-1 shadow-lg"
            >
                {options!.map((opt, i) => (
                    <StatusOptionItem
                        key={opt.id}
                        option={opt}
                        isCurrent={opt.id === current.id}
                        index={i}
                        onSelect={onSelect!}
                    />
                ))}
                {footer && (
                    <>
                        <DropdownMenuSeparator />
                        {footer}
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
